import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardBody, CardFooter, Col, Form, Label, Row } from "reactstrap";
import { toast } from "react-toastify";

import Button from "../../../components/Button/Button";
import Select from "../../../components/Form/Select/Select";
import Input from "../../../components/Form/Input";
import DatePicker from "../../../components/Form/DatePicker/DatePicker";
import labelPlugin from "flatpickr/dist/plugins/labelPlugin/labelPlugin";
import moment from "moment";

import {
  addIndividualInCompany,
  getDependentUserOptions_New,
  getUserProfile,
  getSelectOptions,
} from "../../../helpers/functions_helper";
import { useFormik } from "formik";

import { validationSchema } from "../utils/validation";
import { initialValues } from "../config/initialValues";
import immigrationService from "../services/service";
import { mapImmigrationToForm } from "../mappers/mapper";
import { ImmigrationDataType } from "../types";

interface LocationState {
  immigrationData?: ImmigrationDataType;
}

const FormComponent = (props: any) => {
  const { id, immigration_id } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  let navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  const searchParams = new URLSearchParams(location.search);
  const from = searchParams.get("from");
  const moduleAccess = getUserProfile("immigration");

  const toastId = React.useRef(null) as any;
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState(initialValues);

  const [loadingCompany, setLoadingCompany] = useState(true);
  const [companyOptions, setCompanyOptions] = useState([]);

  const [loadingEmployee, setLoadingEmployee] = useState(false);
  const [disabledEmployee, setDisabledEmployee] = useState(true);
  const [employeeOption, setEmployeeOption] = useState([]);

  const [loadingNationality, setLoadingNationality] = useState(false);
  const [disabledNationality, setDisabledNationality] = useState(true);
  const [nationalityOption, setNationalityOption] = useState([]);

  const [loadingHostCountry, setLoadingHostCountry] = useState(false);
  const [disabledHostCountry, setDisabledHostCountry] = useState(true);
  const [hostCountryOption, setHostCountryOption] = useState([]);

  const [loadingHomeCountry, setLoadingHomeCountry] = useState(false);
  const [disabledHomeCountry, setDisabledHomeCountry] = useState(true);
  const [homeCountryOption, setHomeCountryOption] = useState([]);

  const [loadingAssignmentType, setLoadingAssignmentType] = useState(true);
  const [assignmentTypeOptions, setAssignmentTypeOptions] = useState([]);

  const [loadingImmigrationStatus, setLoadingImmigrationStatus] = useState(false);
  const [immigrationStatusOptions, setImmigrationStatusOptions] = useState([]);

  const { getCompanyOptionsList } = useSelector((state: any) => ({
    getCompanyOptionsList: state.Common.GET_COMPANY_OPTIONS_LIST_SUCCESS_NEW,
  }));

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: values,
    validationSchema: validationSchema,
    onSubmit: (data, { setErrors }) => {
      handleSubmit(data);
    },
  });

  const handleSubmit = (data: any) => {
    if (id && immigration_id) {
      update(data);
    } else {
      create(data);
    }
  };

  const create = async (data: any) => {
    try {
      setLoading(true);
      toastId.current = toast(`${t("Creating")} ${t("Immigration")}...`, {
        autoClose: false,
        isLoading: true,
      });

      const res = (await immigrationService.create(data)) as any;
      if (res.data.status === "success") {
        toast.update(toastId.current, {
          type: toast.TYPE.SUCCESS,
          render: res.data.message,
          autoClose: 5000,
          isLoading: false,
          delay: 500,
        });
        setLoading(false);
        // handleRedirect();
        return navigate(
          `/immigration/create-application/${res.data.data.id}/${res.data.data.immigration_id}?redirect=true`,
          {
            state: {
              immigrationData: res.data.data, // Pass created data here
              createdAt: new Date().getTime(), // Add timestamp to know it's fresh
            },
          },
        );
      } else {
        toast.update(toastId.current, {
          type: toast.TYPE.ERROR,
          render: res.data.message,
          autoClose: 5000,
          isLoading: false,
          delay: 500,
        });

        if (res.data.data) {
          Object.keys(res.data.data).map((key) => {
            toast(res.data.data[key][0], {
              type: toast.TYPE.ERROR,
              autoClose: 5000,
              delay: 500,
            });
          });
        }
        setLoading(false);
      }
    } catch (err: any) {
      toast.update(toastId.current, {
        type: toast.TYPE.ERROR,
        render: err?.message,
        autoClose: 5000,
        isLoading: false,
        delay: 500,
      });
      setLoading(false);
    }
  };

  const update = async (data: any) => {
    try {
      setLoading(true);
      toastId.current = toast(`${t("Updating")} ${t("Immigration")}...`, {
        autoClose: false,
        isLoading: true,
      });

      const res = (await immigrationService.update(data)) as any;
      if (res.data.status === "success") {
        toast.update(toastId.current, {
          type: toast.TYPE.SUCCESS,
          render: res.data.message,
          autoClose: 5000,
          isLoading: false,
          delay: 500,
        });
        setLoading(false);
        // handleRedirect();
        return navigate(
          `/immigration/view-application/${res.data.data.id}/${res.data.data.immigration_id}?redirect=true`,
          {
            state: {
              immigrationData: res.data.data, // Pass created data here
              createdAt: new Date().getTime(), // Add timestamp to know it's fresh
            },
          },
        );
      } else {
        toast.update(toastId.current, {
          type: toast.TYPE.ERROR,
          render: res.data.message,
          autoClose: 5000,
          isLoading: false,
          delay: 500,
        });

        if (res.data.data) {
          Object.keys(res.data.data).map((key) => {
            toast(res.data.data[key][0], {
              type: toast.TYPE.ERROR,
              autoClose: 5000,
              delay: 500,
            });
          });
        }
        setLoading(false);
      }
    } catch (err: any) {
      toast.update(toastId.current, {
        type: toast.TYPE.ERROR,
        render: err?.message,
        autoClose: 5000,
        isLoading: false,
        delay: 500,
      });
      setLoading(false);
    }
  };

  const handleRedirect = (isCancel = false) => {
    if (isCancel === true) {
      if (from) {
        return navigate(`${from}?redirect=true`);
      } else {
        return navigate(`/immigration/list?redirect=true`);
        // return navigate(`/immigration/update-immigration/${id}/${immigration_id}?redirect=true`);
      }
    }
  };

  const handleChange = (fieldName: string, fieldValue: string) => {
    setValues({ ...values, [fieldName]: fieldValue });
    // setAutoSave(true);
  };

  const loadFormData = async () => {
    setLoading(true);

    const res = await immigrationService.get({ id, immigration_id });

    if (res.data.status === "success") {
      const mapped = mapImmigrationToForm(res.data.data);

      setValues((prev: any) => ({
        ...prev,
        ...mapped,
      }));
    }

    setLoading(false);
  };

  useEffect(() => {
    // Check if data was passed via navigation state
    if (state?.immigrationData) {
      const mapped = mapImmigrationToForm(state.immigrationData);

      setValues((prev: any) => ({
        ...prev,
        ...mapped,
      }));

      // No API call needed - data is already loaded
      setLoading(false);
    } else if (id && immigration_id) {
      // Only fetch if no data was passed
      loadFormData();
    }
  }, [state, id, immigration_id]);

  const getExternalUsers = () => {
    setLoadingEmployee(true);

    getDependentUserOptions_New(
      "tbl_master_users",
      "first_name",
      "company",
      parseInt(values?.company_id),
    ).then(function (options) {
      setEmployeeOption(options);
      setLoadingEmployee(false);

      setDisabledEmployee(false);
    });
  };

  useEffect(() => {
    if (getCompanyOptionsList.data) {
      const item = addIndividualInCompany(getCompanyOptionsList.data) as any;
      setCompanyOptions(item);
      setLoadingCompany(false);
    }
  }, [getCompanyOptionsList]);

  useEffect(() => {
    if (values.company_id !== "") {
      getExternalUsers();
    }
  }, [values.company_id]);

  useEffect(() => {
    // Load nationality, host country, home country options
    setLoadingNationality(true);
    setLoadingHostCountry(true);
    setLoadingHomeCountry(true);

    getSelectOptions("tbl_master_country", "name").then(function (options) {
      setNationalityOption(options);
      setHostCountryOption(options);
      setHomeCountryOption(options);

      setLoadingNationality(false);
      setLoadingHostCountry(false);
      setLoadingHomeCountry(false);
    });
  }, []);

  useEffect(() => {
    if (values.employee_id !== "") {
      setDisabledNationality(false);
      setDisabledHostCountry(false);
      setDisabledHomeCountry(false);
    } else {
      setDisabledNationality(true);
      setDisabledHostCountry(true);
      setDisabledHomeCountry(true);
    }
  }, [values.employee_id]);

  useEffect(() => {
    getSelectOptions("tbl_master_assignment_type", "assignment_type_value").then(
      function (options) {
        setAssignmentTypeOptions(options);
        setLoadingAssignmentType(false);
      },
    );
  }, []);

  useEffect(() => {
    getSelectOptions("tbl_master_immigration_status", "immigration_status_value").then(
      function (options) {
        setImmigrationStatusOptions(options);
        setLoadingImmigrationStatus(false);
      },
    );
  }, []);

  return (
    <React.Fragment>
      <Row>
        <Col col="12">
          <Card>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
              }}
              autoComplete="off"
            >
              <CardBody>
                <Row>
                  <Col xl={4} lg={4} md={4} sm={12}>
                    <div className="mb-3">
                      <Select
                        id="company_id"
                        label="Client / Company"
                        options={companyOptions}
                        isClearable={true}
                        isLoading={loadingCompany}
                        isDisabled={loadingCompany}
                        onChange={(e: any) => {
                          if (e) {
                            handleChange("company_id", e.value);
                          } else {
                            handleChange("company_id", "");
                          }
                        }}
                        value={
                          values.company_id
                            ? companyOptions?.find(
                                (option: any) => option.value == values.company_id,
                              )
                            : ""
                        }
                        touched={validation.touched.company_id}
                        error={validation.errors.company_id}
                        required
                      />
                    </div>
                  </Col>

                  <Col xl={8} lg={8} md={8} sm={12}>
                    <div className="mb-3">
                      <Select
                        id="employee_id"
                        label="Employee"
                        options={employeeOption}
                        isClearable={true}
                        isLoading={loadingEmployee}
                        isDisabled={disabledEmployee}
                        onChange={(e: any) => {
                          if (e) {
                            handleChange("employee_id", e.value);
                          } else {
                            handleChange("employee_id", "");
                          }
                        }}
                        value={
                          values.employee_id
                            ? employeeOption?.find(
                                (option: any) => option.value == values.employee_id,
                              )
                            : ""
                        }
                        touched={validation.touched.employee_id}
                        error={validation.errors.employee_id}
                        required
                      />
                    </div>
                  </Col>

                  <Col xl={4} lg={4} md={4} sm={12}>
                    <div className="mb-3">
                      <Select
                        id="nationality_id"
                        label="Nationality"
                        options={nationalityOption}
                        isClearable={true}
                        isLoading={loadingNationality}
                        isDisabled={disabledNationality}
                        onChange={(e: any) => {
                          if (e) {
                            handleChange("nationality_id", e.value);
                          } else {
                            handleChange("nationality_id", "");
                          }
                        }}
                        value={
                          values.nationality_id
                            ? nationalityOption?.find(
                                (option: any) => option.value == values.nationality_id,
                              )
                            : ""
                        }
                        touched={validation.touched.nationality_id}
                        error={validation.errors.nationality_id}
                        required
                      />
                    </div>
                  </Col>

                  <Col xl={4} lg={4} md={4} sm={12}>
                    <div className="mb-3">
                      <Select
                        id="host_country_id"
                        label="Host Country"
                        options={hostCountryOption}
                        isClearable={true}
                        isLoading={loadingHostCountry}
                        isDisabled={disabledHostCountry}
                        onChange={(e: any) => {
                          if (e) {
                            handleChange("host_country_id", e.value);
                          } else {
                            handleChange("host_country_id", "");
                          }
                        }}
                        value={
                          values.host_country_id
                            ? hostCountryOption?.find(
                                (option: any) => option.value == values.host_country_id,
                              )
                            : ""
                        }
                        touched={validation.touched.host_country_id}
                        error={validation.errors.host_country_id}
                        required
                      />
                    </div>
                  </Col>

                  <Col xl={4} lg={4} md={4} sm={12}>
                    <div className="mb-3">
                      <Select
                        id="home_country_id"
                        label="Home Country"
                        options={homeCountryOption}
                        isClearable={true}
                        isLoading={loadingHomeCountry}
                        isDisabled={disabledHomeCountry}
                        onChange={(e: any) => {
                          if (e) {
                            handleChange("home_country_id", e.value);
                          } else {
                            handleChange("home_country_id", "");
                          }
                        }}
                        value={
                          values.home_country_id
                            ? homeCountryOption?.find(
                                (option: any) => option.value == values.home_country_id,
                              )
                            : ""
                        }
                        touched={validation.touched.home_country_id}
                        error={validation.errors.home_country_id}
                        required
                      />
                    </div>
                  </Col>

                  <Col xl={8} lg={8} md={8} sm={12}>
                    <div className="mb-3">
                      <Input
                        id="employer_entity"
                        label="Employer Entity"
                        type="text"
                        onChange={(e: any) => handleChange("employer_entity", e.target.value)}
                        value={values.employer_entity}
                        touched={validation.touched.employer_entity}
                        error={validation.errors.employer_entity}
                      />
                    </div>
                  </Col>

                  <Col className="col-12"></Col>

                  <Col xl={4} lg={4} md={4} sm={12}>
                    <div className="mb-3">
                      <Select
                        id="assignment_type_id"
                        label="Assignment Type"
                        options={assignmentTypeOptions}
                        isClearable={true}
                        isLoading={loadingAssignmentType}
                        isDisabled={loadingAssignmentType}
                        onChange={(e: any) => {
                          if (e) {
                            handleChange("assignment_type_id", e.value);
                          } else {
                            handleChange("assignment_type_id", "");
                          }
                        }}
                        value={
                          values.assignment_type_id
                            ? assignmentTypeOptions?.find(
                                (option: any) => option.value == values.assignment_type_id,
                              )
                            : ""
                        }
                        touched={validation.touched.assignment_type_id}
                        error={validation.errors.assignment_type_id}
                        required
                      />
                    </div>
                  </Col>

                  <Col xl={4} lg={4} md={4} sm={12}>
                    <div className="mb-3">
                      <DatePicker
                        id="assignment_start_date"
                        label="Assignment Start Date"
                        options={{
                          altInput: true,
                          altFormat: "d/m/Y",
                          dateFormat: "Y-m-d",
                          allowInput: true,
                          plugins: [labelPlugin()],
                          onChange: function (selectedDates, dateStr, instance) {
                            handleChange("assignment_start_date", dateStr);
                          },
                          onClose: function (selectedDates, dateStr, instance) {
                            handleChange("assignment_start_date", dateStr);
                          },
                        }}
                        value={values.assignment_start_date}
                        touched={validation.touched.assignment_start_date}
                        error={validation.errors.assignment_start_date}
                        required
                      />
                    </div>
                  </Col>

                  <Col xl={4} lg={4} md={4} sm={12}>
                    <div className="mb-3">
                      <DatePicker
                        id="assignment_end_date"
                        label="Assignment End Date"
                        options={{
                          altInput: true,
                          altFormat: "d/m/Y",
                          dateFormat: "Y-m-d",
                          minDate:
                            values.assignment_start_date &&
                            values.assignment_start_date < moment().format("YYYY-MM-DD hh:mm:ss")
                              ? values.assignment_start_date
                              : "today",
                          allowInput: true,
                          plugins: [labelPlugin()],
                          onChange: function (selectedDates, dateStr, instance) {
                            handleChange("assignment_end_date", dateStr);
                          },
                          onClose: function (selectedDates, dateStr, instance) {
                            handleChange("assignment_end_date", dateStr);
                          },
                        }}
                        value={values.assignment_end_date}
                        touched={validation.touched.assignment_end_date}
                        error={validation.errors.assignment_end_date}
                      />
                    </div>
                  </Col>

                  <Col className="col-12"></Col>

                  <Col xl={4} lg={4} md={4} sm={12}>
                    <div className="mb-3">
                      <Select
                        id="immigration_status_id"
                        label="Immigration Status"
                        options={immigrationStatusOptions}
                        isClearable={true}
                        isLoading={loadingImmigrationStatus}
                        isDisabled={loadingImmigrationStatus}
                        onChange={(e: any) => {
                          if (e) {
                            handleChange("immigration_status_id", e.value);
                          } else {
                            handleChange("immigration_status_id", "");
                          }
                        }}
                        value={
                          values.immigration_status_id
                            ? immigrationStatusOptions?.find(
                                (option: any) => option.value == values.immigration_status_id,
                              )
                            : ""
                        }
                        touched={validation.touched.immigration_status_id}
                        error={validation.errors.immigration_status_id}
                        required
                      />
                    </div>
                  </Col>
                </Row>
              </CardBody>

              <CardFooter className="d-flex flex-wrap gap-2 border-top justify-content-end sticy-footer bg-gray">
                <div className="d-flex gap-2"></div>

                <div className="ms-auto d-flex gap-2 justify-content-end">
                  <Button
                    color="primary"
                    className="btn-block"
                    type="submit"
                    icon="save"
                    loading={loading}
                  >
                    {id && immigration_id ? t("Update") : t("Save & Next")}
                  </Button>

                  <Button
                    color="danger"
                    className="btn-block"
                    type="reset"
                    icon="cancel"
                    loading={loading}
                    onClick={() => handleRedirect(true)}
                  >
                    {t("Cancel")}
                  </Button>
                </div>
              </CardFooter>
            </Form>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default FormComponent;
